import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { STATS } from "./data";

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-[#001E52] min-h-[92vh] flex items-center pt-28 pb-16 md:pt-32"
      data-testid="hero-section"
    >
      {/* Real driving footage background */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/assets/hero-truck.png?v=4"
          data-testid="hero-truck-video"
        >
          <source src="/assets/truck-drive.mp4?v=4" type="video/mp4" />
        </video>
        {/* Golden-hour warm tone over the daytime clip so the truck stays visible with a sunset feel */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(255,170,80,0.28) 0%, rgba(255,120,40,0.18) 48%, rgba(254,106,4,0.4) 100%)",
            mixBlendMode: "soft-light",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(circle at 80% 24%, rgba(255,185,95,0.55) 0%, transparent 46%)",
            mixBlendMode: "screen",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(90deg, rgba(0,17,46,0.9) 0%, rgba(0,23,64,0.55) 42%, rgba(0,30,82,0.18) 72%, rgba(0,30,82,0.02) 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-40"
          style={{ background: "linear-gradient(180deg, transparent, rgba(0,30,82,0.72))" }}
        />
      </div>

      {/* Abstract animated route line */}
      <svg className="absolute inset-0 w-full h-full opacity-20 pointer-events-none z-[1]" preserveAspectRatio="none" viewBox="0 0 1440 800">
        <path className="route-line" d="M-50 640 C 300 520, 520 720, 820 420 C 1060 180, 1260 320, 1500 140" stroke="#FE6A04" strokeWidth="2" fill="none" />
      </svg>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[#FE6A04] mb-6" data-testid="hero-eyebrow">
            Freight Brokerage • Miami Consolidation • Supply Chain Support
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
            Freight Brokerage &amp; Supply Chain Solutions
          </h1>
          <p className="mt-5 font-heading text-xl sm:text-2xl font-semibold text-[#FE6A04]" data-testid="hero-tagline">
            Connecting capacity. Consolidating freight. Delivering reliability.
          </p>
          <p className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
            PrimeLane provides freight brokerage, Miami freight consolidation, warehousing support, sourcing
            assistance, and supply chain solutions for companies that need reliable execution, clear visibility,
            and cost-efficient movement of goods.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              data-testid="hero-quote-cta"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FE6A04] text-white font-semibold hover:bg-[#e15e02] transition-colors"
            >
              Request a Quote <ArrowRight size={18} />
            </a>
            <a
              href="#services"
              data-testid="hero-services-cta"
              className="inline-flex items-center justify-center px-8 py-4 border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors"
            >
              Explore Services
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-12 lg:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/15 border border-white/15 max-w-4xl"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#001E52]/85 backdrop-blur-sm px-6 py-6" data-testid={`hero-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-heading text-2xl sm:text-3xl font-bold text-white">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-slate-400">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
